import { ResumeData } from '../../types';
import { renderMarkdown } from '../../utils/markdownRenderer';

interface ModernTemplateProps {
    data: ResumeData;
}

const formatSectionContent = (content: any): string => {
    if (!content) return '';
    if (typeof content === 'string') return content;
    if (Array.isArray(content)) {
        return content
            .map((entry) => {
                if (!entry) return '';
                if (typeof entry === 'string') return entry;
                const title = entry.title || entry.name || '';
                const url = entry.url ? ` (${entry.url})` : '';
                return `${title}${url}`.trim();
            })
            .filter(Boolean)
            .join(' · ');
    }
    if (typeof content === 'object') {
        const title = content.title || content.name || '';
        const url = content.url ? ` (${content.url})` : '';
        return `${title}${url}`.trim();
    }
    return String(content);
};

export function ModernTemplate({ data }: ModernTemplateProps) {
    const { header, summary, experience, education, skills, projects, settings, customSections, courses, languages, awards } =
        data;
    const spacing = settings.compactSpacing ? 'compact' : 'normal';

    return (
        <div className={`p-12 font-sans ${spacing === 'compact' ? 'text-[10pt]' : 'text-[11pt]'}`}>
            {/* Header */}
            <div className="text-center mb-6 pb-4 border-b-2 border-primary-600">
                <h1 className="text-3xl font-bold text-primary-700 mb-2">{header.fullName || 'Your Name'}</h1>
                <div className="text-sm text-gray-700">
                    {[header.email, header.phone, header.location].filter(Boolean).join(' • ')}
                </div>
                {header.links && header.links.length > 0 && (
                    <div className="text-sm text-gray-600 mt-1">
                        {header.links.map((link, i) => (
                            <span key={i}>
                                {i > 0 && ' • '}
                                {link.url ? (
                                    <a href={link.url} target="_blank" rel="noreferrer" className="underline">
                                        {link.label || link.url}
                                    </a>
                                ) : (
                                    link.label
                                )}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Summary */}
            {summary && (
                <div className={spacing === 'compact' ? 'mb-4' : 'mb-5'}>
                    <h2 className="text-base font-bold text-primary-700 mb-2 pb-1 border-b border-primary-300">
                        PROFESSIONAL SUMMARY
                    </h2>
                    <div className="text-gray-800 text-justify leading-relaxed whitespace-pre-line">{renderMarkdown(summary)}</div>
                </div>
            )}

            {/* Experience */}
            {experience && experience.length > 0 && (
                <div className={spacing === 'compact' ? 'mb-4' : 'mb-5'}>
                    <h2 className="text-base font-bold text-primary-700 mb-2 pb-1 border-b border-primary-300">
                        EXPERIENCE
                    </h2>
                    {experience.map((job, index) => (
                        <div key={job.id} className={index > 0 ? (spacing === 'compact' ? 'mt-3' : 'mt-4') : ''}>
                            <div className="flex justify-between items-baseline mb-1">
                                <div className="font-bold text-gray-900">{job.position}</div>
                                <div className="text-sm text-gray-600 italic">
                                    {job.startDate} - {job.current ? 'Present' : job.endDate || 'Present'}
                                </div>
                            </div>
                            <div className="text-sm text-gray-700 mb-1">
                                {job.company}{job.location && ` • ${job.location}`}
                            </div>
                            {job.bullets && job.bullets.length > 0 && (
                                <ul className="list-disc ml-5 mt-1 text-gray-800">
                                    {job.bullets.map((bullet, i) => (
                                        <li key={i} className="mb-1 leading-relaxed">{bullet}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Education */}
            {education && education.length > 0 && (
                <div className={spacing === 'compact' ? 'mb-4' : 'mb-5'}>
                    <h2 className="text-base font-bold text-primary-700 mb-2 pb-1 border-b border-primary-300">
                        EDUCATION
                    </h2>
                    {education.map((edu, index) => (
                        <div key={edu.id} className={index > 0 ? 'mt-2' : ''}>
                            <div className="flex justify-between items-baseline">
                                <div className="font-bold text-gray-900">{edu.institution}</div>
                                {edu.graduationDate && (
                                    <div className="text-sm text-gray-600 italic">{edu.graduationDate}</div>
                                )}
                            </div>
                            <div className="text-gray-700">
                                {edu.degree}{edu.field && ` in ${edu.field}`}
                            </div>
                            {edu.gpa && (
                                <div className="text-sm text-gray-600">GPA: {edu.gpa}</div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Skills */}
            {skills && skills.categories && skills.categories.length > 0 && (
                <div className={spacing === 'compact' ? 'mb-4' : 'mb-5'}>
                    <h2 className="text-base font-bold text-primary-700 mb-2 pb-1 border-b border-primary-300">
                        SKILLS
                    </h2>
                    {skills.categories.map((category, index) => (
                        <div key={index} className="mb-1">
                            <span className="font-semibold text-gray-900">{category.name}:</span>{' '}
                            <span className="text-gray-800">{category.items.join(', ')}</span>
                        </div>
                    ))}
                </div>
            )}

            {courses && courses.length > 0 && (
                <div className={spacing === 'compact' ? 'mb-4' : 'mb-5'}>
                    <h2 className="text-base font-bold text-primary-700 mb-2 pb-1 border-b border-primary-300">
                        COURSES
                    </h2>
                    {courses.map(course => (
                        <div key={course.id} className="mb-3">
                            <div className="font-bold">
                                {course.course}
                                {course.url && (
                                    <span className="ml-2">
                                        <a href={course.url} target="_blank" rel="noreferrer" className="underline">
                                            {course.institution || course.url}
                                        </a>
                                    </span>
                                )}
                            </div>
                            {course.description && <div className="text-gray-800 whitespace-pre-line">{renderMarkdown(course.description)}</div>}
                            <div className="text-sm text-gray-600">
                                {[course.institution, `${course.startDate || ''}${course.endDate ? ' - ' + course.endDate : ''}`]
                                    .filter(Boolean)
                                    .join(' | ')}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {languages && languages.length > 0 && (
                <div className={spacing === 'compact' ? 'mb-4' : 'mb-5'}>
                    <h2 className="text-base font-bold text-primary-700 mb-2 pb-1 border-b border-primary-300">
                        LANGUAGES
                    </h2>
                    {languages.map(language => (
                        <div key={language.id} className="mb-2">
                            <div className="font-bold">{language.language}</div>
                            {language.level && <div className="text-sm text-gray-600">{language.level}</div>}
                        </div>
                    ))}
                </div>
            )}

            {awards && awards.length > 0 && (
                <div className={spacing === 'compact' ? 'mb-4' : 'mb-5'}>
                    <h2 className="text-base font-bold text-primary-700 mb-2 pb-1 border-b border-primary-300">
                        AWARDS & CERTIFICATIONS
                    </h2>
                    {awards.map(award => (
                        <div key={award.id} className="mb-3">
                            <div className="font-bold">
                                {award.title}
                                {award.url && (
                                    <span className="ml-2">
                                        <a href={award.url} target="_blank" rel="noreferrer" className="underline">
                                            {award.organization || award.url}
                                        </a>
                                    </span>
                                )}
                            </div>
                            {award.organization && (
                                <div className="text-sm text-gray-600">{award.organization}</div>
                            )}
                            {award.date && (
                                <div className="text-xs text-gray-500">Awarded {award.date}</div>
                            )}
                            {award.description && <div className="text-gray-800 whitespace-pre-line">{renderMarkdown(award.description)}</div>}
                        </div>
                    ))}
                </div>
            )}

            {/* Projects */}
            {projects && projects.length > 0 && (
                <div className={spacing === 'compact' ? 'mb-4' : 'mb-5'}>
                    <h2 className="text-base font-bold text-primary-700 mb-2 pb-1 border-b border-primary-300">
                        PROJECTS
                    </h2>
                    {projects.map((project, index) => (
                        <div key={project.id} className={index > 0 ? 'mt-3' : ''}>
                            <div className="font-bold text-gray-900">
                                {project.name}{project.url && ` • ${project.url}`}
                            </div>
                            <div className="text-gray-800 mb-1 whitespace-pre-line">{renderMarkdown(project.description)}</div>
                            {project.highlights && project.highlights.length > 0 && (
                                <ul className="list-disc ml-5 text-gray-800">
                                    {project.highlights.map((highlight, i) => (
                                        <li key={i} className="mb-1">{highlight}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Additional Sections */}
            {customSections && customSections.length > 0 && (
                <div className={spacing === 'compact' ? 'mb-4' : 'mb-5'}>
                    <h2 className="text-base font-bold text-primary-700 mb-2 pb-1 border-b border-primary-300">
                        ADDITIONAL SECTIONS
                    </h2>
                    {customSections.map(section => {
                        const contentText = formatSectionContent(section.content);
                        return (
                            <div key={section.id} className={section.content ? 'mb-3' : 'mb-0'}>
                                <div className="font-bold text-gray-900">{section.title}</div>
                                {section.url && (
                                    <a
                                        href={section.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-sm text-primary-700 underline"
                                    >
                                        {section.url}
                                    </a>
                                )}
                                {section.location && (
                                    <div className="text-xs text-gray-600">{section.location}</div>
                                )}
                                {(section.startDate || section.endDate) && (
                                    <div className="text-xs text-gray-600">
                                        {[section.startDate, section.endDate].filter(Boolean).join(' - ')}
                                    </div>
                                )}
                                {contentText && <div className="text-gray-800 whitespace-pre-line">{renderMarkdown(contentText)}</div>}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}


